import React, { ReactNode, useState, useCallback, useMemo } from 'react';
import { Button, Stack, StackProps, Typography } from '@mui/material';
import { ExpandMore, ExpandLess, UnfoldMore, UnfoldLess } from '@mui/icons-material';

interface Props<T> extends Omit<StackProps, 'children'> {
  /** List of data items */
  items: T[];
  /** Number of items to show initially */
  initialCount: number;
  /** Number of items to reveal per "Show more" click */
  incrementCount?: number;
  /** Whether to show "Show all" button */
  showAllButton?: boolean;
  /** Custom render function for each item. Defaults to Typography with item as string */
  renderItem?: (item: T, index: number) => ReactNode;
  /** Key extractor for list items */
  getKey?: (item: T, index: number) => string | number;
  /** Custom tooltip for "Show more" button */
  showMoreLabel?: (count: number) => string;
  /** Custom tooltip for "Show less" button */
  showLessLabel?: (count: number) => string;
  /** Custom tooltip for "Show all" button */
  showAllLabel?: string;
  /** Custom tooltip for "Collapse all" button */
  collapseAllLabel?: string;
}

const defaultRenderItem = (item: unknown) => <Typography variant="body2">{String(item)}</Typography>;

const defaultGetKey = (_item: unknown, index: number) => index;

export const ExpandableList = <T,>({
  items,
  initialCount,
  showAllButton = false,
  showAllLabel = 'All',
  collapseAllLabel = 'Collapse',
  incrementCount = initialCount,
  showMoreLabel = count => `More (${count})`,
  showLessLabel = count => `Less (${count})`,
  getKey = defaultGetKey as (item: T, index: number) => string | number,
  renderItem = defaultRenderItem as (item: T, index: number) => ReactNode,
  ...stackProps
}: Props<T>) => {
  const [visibleCount, setVisibleCount] = useState(initialCount);

  const totalCount = items.length;
  const isExpanded = visibleCount >= totalCount;
  const canShowMore = visibleCount < totalCount;
  const canShowLess = visibleCount > initialCount;

  const nextIncrementCount = useMemo(
    () => Math.min(incrementCount, totalCount - visibleCount),
    [incrementCount, totalCount, visibleCount]
  );

  const prevDecrementCount = useMemo(
    () => Math.min(incrementCount, visibleCount - initialCount),
    [incrementCount, visibleCount, initialCount]
  );

  const handleShowMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + incrementCount, totalCount));
  }, [incrementCount, totalCount]);

  const handleShowLess = useCallback(() => {
    setVisibleCount(prev => Math.max(prev - incrementCount, initialCount));
  }, [incrementCount, initialCount]);

  const handleShowAll = useCallback(() => {
    setVisibleCount(totalCount);
  }, [totalCount]);

  const handleCollapseAll = useCallback(() => {
    setVisibleCount(initialCount);
  }, [initialCount]);

  const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);

  const renderItems = (list: T[]) =>
    list.map((item, index) => <React.Fragment key={getKey(item, index)}>{renderItem(item, index)}</React.Fragment>);

  if (totalCount <= initialCount) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5} {...stackProps}>
        {renderItems(items)}
      </Stack>
    );
  }

  return (
    <Stack gap={0.5} {...stackProps}>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {renderItems(visibleItems)}
      </Stack>

      <Stack direction="row" gap={0.5} alignItems="center">
        {canShowMore && (
          <Button size="small" startIcon={<ExpandMore />} onClick={handleShowMore}>
            {showMoreLabel(nextIncrementCount)}
          </Button>
        )}

        {showAllButton && canShowMore && (
          <Button size="small" startIcon={<UnfoldMore />} onClick={handleShowAll}>
            {showAllLabel}
          </Button>
        )}

        {canShowLess && (
          <Button size="small" startIcon={isExpanded ? <UnfoldLess /> : <ExpandLess />} onClick={handleShowLess}>
            {showLessLabel(prevDecrementCount)}
          </Button>
        )}

        {isExpanded && (
          <Button size="small" startIcon={<UnfoldLess />} onClick={handleCollapseAll}>
            {collapseAllLabel}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
